
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { validateContentForm, submitContentToSupabase, INBOX_ID } from "@/utils/contentFormUtils";
import { validateCollectionForExtension } from "@/utils/extensionUtils";
import { useThumbnailExtraction } from "./useThumbnailExtraction";
import { useExtensionContentTracking } from "./useExtensionContentTracking";
import { supabase } from "@/integrations/supabase/client";

interface InitialFormData {
  id?: string;
  url?: string;
  title?: string;
  description?: string;
  content_type?: string;
  collection_id?: string;
  archived?: boolean;
  fromExtension?: boolean;
}

interface UseContentFormProps {
  onComplete?: () => void;
  initialData?: InitialFormData;
  isUpdate?: boolean;
}

export const useContentForm = ({ 
  onComplete, 
  initialData = {}, 
  isUpdate = false 
}: UseContentFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [url, setUrl] = useState(initialData.url || "");
  const [title, setTitle] = useState(initialData.title || "");
  const [description, setDescription] = useState(initialData.description || "");
  const [contentType, setContentType] = useState<string>(initialData.content_type || "webpage");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // Handle collection selection with proper extension validation
  const [selectedCollection, setSelectedCollection] = useState<string>(() => {
    if (initialData.fromExtension) {
      // For extension content, validate the collection ID
      return validateCollectionForExtension(initialData.collection_id || INBOX_ID);
    }
    return initialData.collection_id || INBOX_ID;
  });
  
  const [initialUrl] = useState(initialData.url || "");
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { extractThumbnailForContent } = useThumbnailExtraction();
  const { trackExtensionContentSaved } = useExtensionContentTracking();

  // Handle tag toggle
  const handleTagToggle = (tagId: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  // Optimized analytics tracking function - non-blocking
  const trackContentSaved = async (contentId: string) => {
    if (!user) {
      console.log('[Analytics] No user found, skipping tracking');
      return;
    }

    // Run analytics in background without blocking
    setTimeout(async () => {
      try {
        console.log('[Analytics] Starting to track content_saved event for:', contentId);
        
        const sourceMethod = initialData.fromExtension ? 'extension' : 'manual';
        
        const eventData = {
          content_id: contentId,
          content_type: contentType,
          source_method: sourceMethod,
          has_title: Boolean(title?.trim()),
          has_description: Boolean(description?.trim()),
          collection_id: selectedCollection === INBOX_ID ? 'inbox' : selectedCollection
        };

        console.log('[Analytics] Event data prepared:', eventData);

        const eventType = initialData.fromExtension ? 'content_saved_extension' : 'content_saved';

        const insertData = {
          event_type: eventType,
          event_category: 'content_saving',
          event_data: eventData,
          user_id: user.id,
          session_id: null,
          page_path: window.location.pathname,
          timestamp: new Date().toISOString()
        };

        console.log('[Analytics] About to insert:', insertData);

        const { data, error } = await supabase
          .from('user_activity_events')
          .insert(insertData)
          .select();

        if (error) {
          console.error('[Analytics] Error tracking content_saved:', error);
          console.error('[Analytics] Error details:', error.message, error.details);
        } else {
          console.log('[Analytics] Successfully tracked content_saved event:', eventData);
          console.log('[Analytics] Database response:', data);
        }
      } catch (error) {
        console.error('[Analytics] Failed to track content_saved event:', error);
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('[Form] Starting form submission, isUpdate:', isUpdate);
    console.log('[Form] Selected collection:', selectedCollection);
    console.log('[Form] From extension:', initialData.fromExtension);
    
    if (!validateContentForm(url, contentType, user)) {
      console.log('[Form] Validation failed');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Ensure collection is valid for extension saves
      const validatedCollection = initialData.fromExtension 
        ? validateCollectionForExtension(selectedCollection)
        : selectedCollection;
        
      console.log("Submitting form with validated collection:", validatedCollection);
      
      const { success, contentId } = await submitContentToSupabase({
        user,
        url,
        title: title.trim() || "", 
        description: description.trim() || "", 
        contentType,
        selectedCollection: validatedCollection,
        isUpdate,
        contentId: initialData.id,
      });
      
      console.log('[Form] Submission result - success:', success, 'contentId:', contentId);
      
      if (success && contentId) {
        // Track content saved event ONLY for new content (not updates) - non-blocking
        if (!isUpdate) {
          console.log('[Analytics] About to track content saved for ID:', contentId);
          console.log('[Analytics] User ID:', user?.id);
          trackContentSaved(contentId);
        } else {
          console.log('[Analytics] Skipping tracking for update');
        }

        if (!isUpdate) {
          setUrl("");
          setTitle("");
          setDescription("");
          setContentType("webpage");
          setSelectedCollection(INBOX_ID);
          setSelectedTags([]);
        }
        
        // Batch query invalidations for better performance
        const invalidationPromises = [
          queryClient.invalidateQueries({ queryKey: ['contentItems'] }),
          queryClient.invalidateQueries({ queryKey: ['collections'] }),
          queryClient.invalidateQueries({ queryKey: ['collectionContent'] })
        ];
        
        // Run invalidations in background
        Promise.all(invalidationPromises).catch(error => {
          console.error('Error invalidating queries:', error);
        });
        
        // Extract thumbnail in background if it's a new item or if the URL has changed
        if (contentId && (!isUpdate || url !== initialUrl)) {
          // Don't await - run in background
          setTimeout(async () => {
            try {
              console.log(`Extracting thumbnail for ${isUpdate ? "updated" : "new"} content with soft clip level:`, contentId);
              await extractThumbnailForContent(contentId, 'soft');
            } catch (error) {
              console.error("Error extracting thumbnail:", error);
            }
          }, 0);
        }
        
        // Save selected tags to the database
        if (contentId && selectedTags.length > 0) {
          setTimeout(async () => {
            try {
              console.log(`Adding tags to content ${contentId}:`, selectedTags);
              
              // Add each selected tag to the content
              const tagPromises = selectedTags.map(async (tagId) => {
                const { error } = await supabase
                  .from('content_tags')
                  .insert([{ content_id: contentId, tag_id: tagId }]);
                
                if (error) {
                  console.error(`Error adding tag ${tagId} to content:`, error);
                }
              });
              
              await Promise.all(tagPromises);
              console.log('All tags added successfully');
            } catch (error) {
              console.error("Error adding tags to content:", error);
            }
          }, 0);
        }
        
        if (onComplete) {
          onComplete();
        } else if (!isUpdate) {
          navigate('/dashboard', { replace: true });
        }
      } else {
        console.log('[Form] Submission failed - no contentId returned');
      }
    } catch (error) {
      console.error('[Form] Error during submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formState: {
      url,
      title,
      description,
      contentType,
      selectedCollection,
      selectedTags,
      isSubmitting
    },
    setters: {
      setUrl,
      setTitle,
      setDescription,
      setContentType,
      setSelectedCollection: (collectionId: string) => {
        // Validate collection ID if this is from extension
        const validatedId = initialData.fromExtension 
          ? validateCollectionForExtension(collectionId)
          : collectionId;
        setSelectedCollection(validatedId);
      }
    },
    handleSubmit,
    handleTagToggle
  };
};
